// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import type { ActiveTab, DeepLinkableTab, NavParams } from '../types/navigation';

/**
 * Build a URL hash string for a tab with optional deep-link parameters.
 *
 * @example buildNavHash('assessments', { controlId: '1.1.2' }) => '#assessments?controlId=1.1.2'
 * @example buildNavHash('dashboard') => '#dashboard'
 */
export function buildNavHash<T extends DeepLinkableTab>(tab: T, params?: NavParams[T]): string;
export function buildNavHash(tab: ActiveTab): string;
export function buildNavHash(tab: string, params?: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== '') search.set(k, v);
    }
  }
  const qs = search.toString();
  return qs ? `#${tab}?${qs}` : `#${tab}`;
}

/**
 * Parse a URL hash into tab name + query parameters.
 *
 * @example parseNavHash('#assessments?controlId=1.1.2') => { tab: 'assessments', params: { controlId: '1.1.2' } }
 * @returns null when hash is empty or '#'
 */
export function parseNavHash(hash: string): { tab: string; params: Record<string, string> } | null {
  if (!hash || hash === '#') return null;
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const qIdx = raw.indexOf('?');
  const tab = qIdx >= 0 ? raw.slice(0, qIdx) : raw;
  const params: Record<string, string> = {};
  if (qIdx >= 0) {
    const sp = new URLSearchParams(raw.slice(qIdx + 1));
    sp.forEach((v, k) => { params[k] = v; });
  }
  return { tab, params };
}

/**
 * Open a deep-linked reference in a new browser tab.
 * Used for referential navigation (detail row badges, deployment action buttons).
 * Structural (same-tab) navigation should use the onNavigate callback instead.
 */
export function openReference<T extends DeepLinkableTab>(tab: T, params?: NavParams[T]): void {
  const hash = buildNavHash(tab, params);
  window.open(`${window.location.pathname}${hash}`, '_blank', 'noopener');
}
